import type { CombinationCell, CombinationExperimentInfo, CombinationInteraction } from 'statisticsdb/types';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { IconInfoCircle } from '@tabler/icons-react';
import { cn } from 'helpers/cn';
import { formatNumber } from 'helpers/formatters';

/**
 * One row per variant combination, with the interaction between the two changes.
 *
 * Deliberately no conversion rate. Rate needs exposure, and exposure per combination is not
 * measurable: impressions are retained for about two weeks against years of conversions, the
 * runtime emits one per experiment per pageview rather than per visitor, and the variant map
 * fills up during a visit so early pageviews carry partial maps.
 *
 * What is measurable from conversions alone is the interaction odds ratio, because allocation
 * cancels out of it, and average order value, because revenue and conversions come from the
 * same conversions. So the table reports those and says plainly that it is not ranking by
 * rate.
 */
type InteractionTableProps = {
  combinations: CombinationCell[];
  interactions: CombinationInteraction[];
  experiments: CombinationExperimentInfo[];
  interactionAvailable: boolean;
  window: { from: number; to: number } | null;
};

/** Fixed widths for the measure columns; the variant columns take the rest. */
const NUMBER_COLUMN = 116;
const INTERACTION_COLUMN = 168;
const VARIANT_COLUMN_MIN = 180;

const day = (ms: number) => new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

const variantName = (experiments: CombinationExperimentInfo[], experimentId: number, variantId: number): string =>
  experiments.find((e) => e.id === experimentId)?.variants.find((v) => v.id === variantId)?.name ?? `#${variantId}`;

/** Reads the ratio as a sentence, since "1.05" alone tells a reader nothing. */
const describe = (interaction: CombinationInteraction): string => {
  const pct = Math.abs((interaction.oddsRatio - 1) * 100).toFixed(1);
  if (!interaction.significant) {
    return `No interaction detected. The interval ${interaction.ciLower.toFixed(2)}-${interaction.ciUpper.toFixed(2)} includes 1, so the two changes behave independently as far as this data can tell.`;
  }
  return interaction.oddsRatio > 1
    ? `These two changes reinforce each other: together they do about ${pct}% better on the odds scale than their individual effects predict.`
    : `These two changes interfere: together they do about ${pct}% worse on the odds scale than their individual effects predict.`;
};

export const InteractionTable = ({
  combinations,
  interactions,
  experiments,
  interactionAvailable,
  window,
}: InteractionTableProps) => {
  if (combinations.length === 0) return null;

  const byKey = new Map(interactions.map((i) => [i.combinationKey, i]));
  const columns = combinations[0]!.variants.map((v) => v.experimentId);
  const best = interactions
    .filter((i) => i.significant && i.oddsRatio > 1)
    .sort((a, b) => b.oddsRatio - a.oddsRatio)[0];

  // Head and body share one template, so the columns cannot drift apart, and one
  // min-width makes the whole table scroll rather than crush the variant names.
  const measures = interactionAvailable
    ? [NUMBER_COLUMN, NUMBER_COLUMN, NUMBER_COLUMN, INTERACTION_COLUMN]
    : [NUMBER_COLUMN, NUMBER_COLUMN, NUMBER_COLUMN];
  const gridTemplateColumns = [
    ...columns.map(() => `minmax(${VARIANT_COLUMN_MIN}px, 1fr)`),
    ...measures.map((width) => `${width}px`),
  ].join(' ');
  const minWidth = columns.length * VARIANT_COLUMN_MIN + measures.reduce((total, width) => total + width, 0);

  return (
    <TooltipProvider>
      <section className="bg-panel border-line rounded-panel border" data-tour="combination-results">
        <div className="border-line space-y-2 border-b px-3 py-2.5">
          <h2 className="text-body font-semibold tracking-tight">Combination performance</h2>

          {/*
            Two columns rather than one narrow one. A single measure capped for
            readability left most of a very wide card empty, but letting lines run the
            full width would be unreadable. Columns use the space and keep each line
            short, collapsing to one column when there is no room for two.
          */}
          {/* `basis-full` below `sm`, or `flex-1`'s 0% basis means the two columns always
              "fit" and never wrap — two 160px columns of prose on a phone. */}
          <div className="flex flex-wrap gap-x-10 gap-y-2">
            <p className="supporting max-w-xl basis-full sm:flex-1 sm:basis-0">
              Conversions and order value per combination, with the interaction between the two changes. There is no
              conversion rate here: that needs to know how much traffic each combination received, which is not
              recorded. The interaction is measurable without it, because traffic allocation cancels out of the ratio.
            </p>
            {window && (
              <p className="supporting max-w-xl basis-full sm:flex-1 sm:basis-0">
                Counted over{' '}
                <span className="text-foreground font-medium">
                  {day(window.from)} to {day(window.to)}
                </span>
                , the period both experiments were randomising. Traffic after a variant was deployed is excluded, since
                everyone receives it from then on.
              </p>
            )}
          </div>
        </div>

        <div className="overflow-x-auto p-3">
          <div style={{ minWidth }}>
            <div
              className="border-line bg-panel2 rounded-card grid items-end gap-x-3 border border-l-2 border-l-transparent px-2.5 py-2"
              style={{ gridTemplateColumns }}
            >
              {columns.map((experimentId) => (
                <div key={experimentId} className="min-w-0">
                  <span className="text-foreground block truncate text-meta font-medium">
                    {experiments.find((e) => e.id === experimentId)?.name ?? `Experiment ${experimentId}`}
                  </span>
                  <span className="micro-label">variant</span>
                </div>
              ))}
              <span className="micro-label text-right">Conversions</span>
              <span className="micro-label text-right">Revenue</span>
              <span className="micro-label flex items-center justify-end gap-1">
                Order value
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconInfoCircle className="text-dim size-3.5" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Revenue divided by conversions. Comparable across combinations without knowing traffic, because both
                    come from the same set of conversions.
                  </TooltipContent>
                </Tooltip>
              </span>
              {interactionAvailable && (
                <span className="micro-label text-right" data-tour="combination-interaction">
                  Interaction
                </span>
              )}
            </div>

            <div className="mt-1 flex flex-col gap-0.5">
              {combinations.map((combo) => {
                const interaction = byKey.get(combo.combinationKey);
                const isBest = best && combo.combinationKey === best.combinationKey;

                return (
                  <div
                    key={combo.combinationKey}
                    className={cn(
                      'hover:bg-hover rounded-card grid items-center gap-x-3 border-l-2 border-l-transparent px-2.5 py-2 transition-colors duration-150',
                      combo.isControl && 'bg-panel2',
                      // The strongest reinforcing combination is the one finding on the
                      // table, so it takes a rule rather than a fill: the control row is
                      // already carrying the only fill here.
                      isBest && 'border-l-ok',
                    )}
                    style={{ gridTemplateColumns }}
                  >
                    {combo.variants.map((v, index) => (
                      <span
                        key={v.experimentId}
                        className={cn('flex min-w-0 items-center gap-1.5 text-body', combo.isControl && 'font-medium')}
                        title={variantName(experiments, v.experimentId, v.variantId)}
                      >
                        <span className="min-w-0 truncate">
                          {variantName(experiments, v.experimentId, v.variantId)}
                        </span>
                        {/*
                          The teal left rule was the entire "best" signal — no text, no
                          legend — so the table's one finding was invisible in greyscale,
                          to a red-green reader, and to a screen reader. The chip says it
                          once, on the first cell, next to the control row's own word.
                        */}
                        {isBest && index === 0 && (
                          <span className="micro-label border-ok-line text-ok shrink-0 rounded-pill border px-1.5">
                            best
                          </span>
                        )}
                      </span>
                    ))}

                    <span className="num text-right">{formatNumber(combo.conversions)}</span>
                    <span className="num text-right">{formatNumber(combo.revenue / 100)}</span>
                    <span className="num text-right">{formatNumber(combo.avgOrderValue)}</span>

                    {interactionAvailable && (
                      <span className="text-right">
                        {combo.isControl ? (
                          <span className="micro-label">baseline</span>
                        ) : !interaction ? (
                          // Margins of the table are the ratio's own denominators, and a zero
                          // cell makes the standard error undefined.
                          <span className="micro-label">-</span>
                        ) : (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="inline-flex cursor-help flex-col items-end gap-0.5">
                                <span
                                  className={cn(
                                    'num font-medium',
                                    !interaction.significant
                                      ? 'text-dim'
                                      : interaction.oddsRatio > 1
                                        ? 'text-ok'
                                        : 'text-danger',
                                  )}
                                >
                                  {interaction.oddsRatio.toFixed(3)}×
                                </span>
                                <span className="micro-label tracking-normal">
                                  {interaction.ciLower.toFixed(2)} to {interaction.ciUpper.toFixed(2)}
                                </span>
                              </span>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm space-y-2">
                              <p>{describe(interaction)}</p>
                              <p className="text-dim">
                                p = {interaction.pValue.toFixed(4)}. Built from four cells: {interaction.cells.variant}{' '}
                                both variants, {interaction.cells.controlB} and {interaction.cells.variantB} one each,{' '}
                                {interaction.cells.control} neither.
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="border-line border-t px-3 py-2.5">
          {interactionAvailable ? (
            <p className="supporting max-w-3xl">
              Interaction is an odds ratio against the baseline combination. 1 means the two changes are independent, so
              their combined effect is just the product of their separate effects. Above 1 they reinforce each other,
              below 1 they interfere. The range is a 95% interval; one that includes 1 is not a finding.
            </p>
          ) : (
            <p className="supporting max-w-3xl">
              Interaction is only reported for two experiments at a time. Beyond that the cells hold too few conversions
              for the result to mean anything.
            </p>
          )}
        </div>
      </section>
    </TooltipProvider>
  );
};
